import '@/shared/styles/ActionDock.css';
import {TOAST_CONFIG} from '@/shared/constants.ts';
import {LucideIcon} from 'lucide-react';
import {memo, useCallback} from 'react';

export type ActionDockItem = {
    id: string;
    label: string;
    icon: LucideIcon;
    onClick: () => void;
    color?: string;
};

type ActionDockProps = {
    actions: ActionDockItem[];
    isOpen: boolean;
    onClose: () => void;
};

export const ActionDock = memo(({actions, isOpen, onClose}: ActionDockProps) => {
    const handleActionClick = useCallback(
        (action: ActionDockItem) => {
            if ('vibrate' in navigator) {
                navigator.vibrate(TOAST_CONFIG.HAPTIC_DURATION_MS);
            }
            action.onClick();
            onClose();
        },
        [onClose]
    );

    if (!isOpen) {
        return null;
    }

    return (
        <>
            {/* Overlay with blur */}
            <div
                className="fixed inset-0 z-[99] bg-black/20 backdrop-blur-[2px] animate-fadeIn"
                onClick={onClose}
                data-testid="action-dock-overlay"
            />

            {/* Action Menu */}
            <div
                className="fixed bottom-[calc(var(--dock-height)+var(--safe-area-bottom)+80px)] right-4 z-[100] flex flex-col gap-2 animate-slideUp"
                data-testid="action-dock"
            >
                {actions.map((action) => {
                    const Icon = action.icon;
                    return (
                        <button
                            key={action.id}
                            onClick={() => handleActionClick(action)}
                            className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-secondary)] rounded-xl shadow-lg border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] active:scale-95 transition-all"
                            data-testid={`action-dock-item-${action.id}`}
                        >
                            <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center"
                                style={{
                                    backgroundColor: action.color
                                        ? `${action.color}20`
                                        : 'var(--accent-soft)',
                                    color: action.color || 'var(--accent)',
                                }}
                            >
                                <Icon size={20} />
                            </div>
                            <span className="text-sm font-semibold text-[var(--text-primary)] whitespace-nowrap">
                                {action.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </>
    );
});

ActionDock.displayName = 'ActionDock';
